import { useState, useEffect, useMemo, useRef } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  BookOpen,
  Search,
  Printer,
  CheckCircle2,
  Pencil,
  Loader2,
  ChevronRight,
  X,
} from "lucide-react";
import api from "../lib/api";

const Handbook = () => {
  const { isAdmin } = useAuth();
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // section object
  const [editForm, setEditForm] = useState({ title: "", content: "" });
  const [saving, setSaving] = useState(false);
  const [acked, setAcked] = useState(null); // {acknowledged, acknowledged_at}
  const [acking, setAcking] = useState(false);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  const contentRef = useRef(null);

  const loadSections = async () => {
    try {
      const res = await api.get("/handbook/sections");
      setSections(res.data);
      if (res.data.length && !activeId) setActiveId(res.data[0].id);
    } catch {
      toast.error("Failed to load handbook");
    } finally {
      setLoading(false);
    }
  };

  const loadAck = async () => {
    try {
      const res = await api.get("/handbook/acknowledgments/me");
      setAcked(res.data);
    } catch {
      setAcked({ acknowledged: false });
    }
  };

  useEffect(() => {
    loadSections();
    loadAck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredSections = useMemo(() => {
    if (!search.trim()) return sections;
    const q = search.toLowerCase();
    return sections.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.content.toLowerCase().includes(q),
    );
  }, [search, sections]);

  const activeSection = sections.find((s) => s.id === activeId);

  const handleSelect = (id) => {
    setActiveId(id);
    setMobileTocOpen(false);
    if (contentRef.current) contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openEdit = (section) => {
    setEditing(section);
    setEditForm({ title: section.title, content: section.content });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/handbook/sections/${editing.id}`, editForm);
      setSections((prev) =>
        prev.map((s) => (s.id === editing.id ? res.data : s)),
      );
      toast.success("Section updated");
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleAck = async () => {
    setAcking(true);
    try {
      const res = await api.post("/handbook/acknowledge");
      setAcked({ acknowledged: true, ...res.data });
      toast.success("Thanks — your acknowledgment is on file");
    } catch {
      toast.error("Failed to record acknowledgment");
    } finally {
      setAcking(false);
    }
  };

  const handlePrint = () => window.print();

  // Highlight matching text in content when searching
  const renderContent = (text) => {
    const paragraphs = text.split(/\n{2,}/);
    return paragraphs.map((p, i) => (
      <p key={i} className="mb-4 whitespace-pre-line leading-relaxed">
        {p}
      </p>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="handbook-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
            <BookOpen size={24} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              Employee Handbook
            </h1>
            <p className="text-sm text-muted-foreground">
              Policies, practices &amp; procedures · Steele Martin Jones &amp; Co.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {acked?.acknowledged && (
            <Badge
              className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 gap-1.5"
              data-testid="ack-badge"
            >
              <CheckCircle2 size={14} />
              Acknowledged
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            data-testid="handbook-print-btn"
          >
            <Printer size={16} className="mr-1.5" />
            Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="sm:hidden"
            onClick={() => setMobileTocOpen(true)}
          >
            Contents
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-xl">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          placeholder="Search policies… e.g. PTO, dress code, jury duty"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="handbook-search"
        />
      </div>

      {/* Layout: TOC sidebar + content */}
      <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr] gap-6">
        {/* TOC sidebar — desktop */}
        <aside className="hidden sm:block">
          <Card className="sticky top-4">
            <CardContent className="p-3 max-h-[calc(100vh-180px)] overflow-y-auto">
              <ul className="space-y-0.5">
                {filteredSections.length === 0 && (
                  <li className="text-xs text-muted-foreground p-2">
                    No matches
                  </li>
                )}
                {filteredSections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => handleSelect(s.id)}
                      className={`group w-full flex items-center gap-2 text-left text-sm rounded-md px-2.5 py-1.5 transition-colors ${
                        s.id === activeId
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                      }`}
                      data-testid={`toc-item-${s.order}`}
                    >
                      <span className="text-xs tabular-nums opacity-60 w-5 shrink-0">
                        {s.order}.
                      </span>
                      <span className="truncate">{s.title}</span>
                      {s.id === activeId && (
                        <ChevronRight size={14} className="ml-auto shrink-0" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </aside>

        {/* Main content */}
        <div
          ref={contentRef}
          className="max-w-3xl print:max-w-none"
          data-testid="handbook-content"
        >
          {activeSection ? (
            <Card>
              <CardContent className="p-6 sm:p-10">
                <div className="flex items-start gap-3 mb-6">
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
                      Section {activeSection.order}
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                      {activeSection.title}
                    </h2>
                    {activeSection.updated_by_name && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Last updated by {activeSection.updated_by_name} ·{" "}
                        {new Date(activeSection.updated_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(activeSection)}
                      data-testid="handbook-edit-btn"
                      className="print:hidden"
                    >
                      <Pencil size={14} className="mr-1" />
                      Edit
                    </Button>
                  )}
                </div>

                <div className="text-foreground text-[15px] sm:text-base">
                  {renderContent(activeSection.content)}
                </div>

                {/* Acknowledgment CTA — on the last section */}
                {activeSection.order === sections.length && (
                  <div className="mt-8 pt-6 border-t border-border print:hidden">
                    {acked?.acknowledged ? (
                      <div className="flex items-start gap-3 p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                        <CheckCircle2
                          size={20}
                          className="text-emerald-500 shrink-0 mt-0.5"
                        />
                        <div className="text-sm">
                          <p className="font-medium text-foreground">
                            You acknowledged this handbook on{" "}
                            {new Date(
                              acked.acknowledged_at,
                            ).toLocaleDateString()}
                          </p>
                          <p className="text-muted-foreground mt-0.5">
                            HR has a record of your acknowledgment.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Click below to electronically acknowledge that you've
                          read and understood the handbook.
                        </p>
                        <Button
                          onClick={handleAck}
                          disabled={acking}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="handbook-acknowledge-btn"
                        >
                          {acking ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Recording…
                            </>
                          ) : (
                            <>
                              <CheckCircle2 size={16} className="mr-2" />
                              I've read &amp; acknowledge this handbook
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Next-section button */}
                {activeSection.order < sections.length && (
                  <div className="mt-8 pt-6 border-t border-border print:hidden">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const next = sections.find(
                          (s) => s.order === activeSection.order + 1,
                        );
                        if (next) handleSelect(next.id);
                      }}
                      data-testid="handbook-next-btn"
                    >
                      Next:{" "}
                      {
                        sections.find(
                          (s) => s.order === activeSection.order + 1,
                        )?.title
                      }
                      <ChevronRight size={16} className="ml-1.5" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-10 text-center text-muted-foreground">
                Select a section from the table of contents
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Mobile TOC drawer */}
      {mobileTocOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 sm:hidden"
          onClick={() => setMobileTocOpen(false)}
        >
          <div
            className="absolute left-0 top-0 h-full w-72 bg-card border-r border-border p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Contents</h3>
              <button onClick={() => setMobileTocOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <ul className="space-y-0.5">
              {sections.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => handleSelect(s.id)}
                    className={`w-full text-left text-sm rounded-md px-2 py-1.5 ${
                      s.id === activeId
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-muted-foreground"
                    }`}
                  >
                    <span className="opacity-60">{s.order}.</span> {s.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Edit dialog (admin) */}
      <Dialog
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
      >
        <DialogContent className="sm:max-w-2xl" data-testid="handbook-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Handbook Section</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Title
              </label>
              <Input
                value={editForm.title}
                onChange={(e) =>
                  setEditForm({ ...editForm, title: e.target.value })
                }
                data-testid="handbook-edit-title"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Content (plain text; blank line separates paragraphs)
              </label>
              <Textarea
                value={editForm.content}
                onChange={(e) =>
                  setEditForm({ ...editForm, content: e.target.value })
                }
                rows={20}
                className="font-mono text-sm"
                data-testid="handbook-edit-content"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button
                onClick={saveEdit}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="handbook-edit-save"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving…
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Handbook;
